"use client";

import { useEffect, useMemo, useState } from "react";
import { MessageSquareText, CircleX, Loader2, ChevronLeft } from "lucide-react";
import { useToast } from "@/components/ui/feedback/ToastProvider";
import { api } from "@/trpc/react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/shadcn/dialog";

type FeedbackProps = {
  triggeredBy?: string;
  label?: string;
  className?: string;
};

type FeedbackDialogProps = {
  open: boolean;
  onClose: () => void;
  onBack?: () => void;
  triggeredBy?: string;
  title?: string;
};

export function FeedbackDialog({
  open,
  onClose,
  onBack,
  triggeredBy,
  title = "Send feedback",
}: FeedbackDialogProps) {
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToast();

  const isValid = useMemo(() => message.trim().length >= 10, [message]);

  const feedbackMutation = api.feedback.submit.useMutation({
    onSuccess: () => {
      showToast("Thanks for the feedback!");
      onClose();
    },
    onError: (e) => {
      const msg = e?.message ?? "Failed to send. Please try again.";
      setError(msg);
      showToast(msg, "error");
    },
  });

  useEffect(() => {
    if (!open) {
      setError(null);
      setSubmitting(false);
      setMessage("");
    }
  }, [open]);

  async function submitFeedback() {
    if (!isValid) return;
    setError(null);
    setSubmitting(true);
    try {
      await feedbackMutation.mutateAsync({
        message: message.trim(),
        triggeredBy: triggeredBy ?? "support:feedback",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="mx-4 w-full max-w-md overflow-hidden rounded-2xl border border-[hsl(var(--brand-violet-300))]/50 bg-background shadow-xl sm:mx-0 sm:max-w-lg p-0 gap-0 [&>button]:hidden">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div className="flex items-center gap-2">
            {onBack && (
              <button
                aria-label="Back"
                onClick={onBack}
                className="rounded-sm p-1 text-muted-foreground hover:text-foreground hover:bg-transparent focus:outline-none"
              >
                <ChevronLeft className="h-5 w-5 opacity-70 transition-opacity hover:opacity-100" />
              </button>
            )}
            <DialogTitle className="text-base font-semibold">{title}</DialogTitle>
          </div>
          <button
            aria-label="Close"
            onClick={onClose}
            className="rounded-sm p-1 text-muted-foreground hover:text-foreground hover:bg-transparent focus:outline-none"
          >
            <CircleX className="h-5 w-5 opacity-70 transition-opacity hover:opacity-100" />
          </button>
        </div>

        <div className="px-5 py-5">
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Your feedback
          </label>
          <textarea
            rows={7}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            className={[
              "w-full rounded-lg border bg-background p-3 text-sm outline-none",
              "focus:ring focus:ring-[hsl(var(--brand-violet-300))]",
              error ? "border-red-500 focus:ring-red-500" : "",
            ].join(" ")}
            placeholder="What's working? What's confusing? What would you improve?"
          />

          {error && (
            <p className="mt-2 text-xs text-red-500" role="alert">
              {error}
            </p>
          )}

          <div className="mt-5 flex justify-end gap-2">
            <button
              onClick={onClose}
              className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
            >
              Cancel
            </button>
            <button
              onClick={submitFeedback}
              disabled={!isValid || submitting}
              className="inline-flex items-center gap-2 rounded-md bg-foreground px-3 py-1.5 text-sm text-background disabled:opacity-50"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Feedback({ triggeredBy, label = "Feedback", className }: FeedbackProps) {
  const [open, setOpen] = useState(false);
  const openModal = () => setOpen(true);
  const closeModal = () => setOpen(false);

  return (
    <>
      <button
        onClick={openModal}
        className={[
          "relative inline-flex items-center gap-2 rounded-md border",
          "border-[hsl(var(--brand-violet-300))]/50 bg-muted px-3 py-1.5 text-sm font-medium text-foreground",
          "shadow transition-all hover:bg-accent focus:outline-none focus:ring focus:ring-muted-foreground/50",
          className || "",
        ].join(" ")}
      >
        <MessageSquareText size={18} />
        {label}
      </button>

      <FeedbackDialog open={open} onClose={closeModal} triggeredBy={triggeredBy} />
    </>
  );
}
